import { useEffect, useRef } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import { AccountMapper } from '@/core/api/mappers/account/accountMapper'
import { UserMapper } from '@/core/api/mappers/userMapper'
import { accountService } from '@/core/api/services/account/accountService'
import { FULL_ACCOUNT_INCLUDES } from '@/core/api/services/account/constants'
import userService from '@/core/api/services/userService'
import { RootState } from '@/core/store'
import { setUser } from '@/core/store/slices/auth'
import {
	SELECTED_ACCOUNT_ID_KEY,
	setSelectedAccount,
} from '@/core/store/slices/selectedAccountSlice'

/**
 * Hook to initialize auth state by fetching user data once on app load
 * This should only be called ONCE at the app root level
 */
export const useAuthInitializer = () => {
	const dispatch = useDispatch()
	const { token, user } = useSelector((state: RootState) => state.auth)
	const hasFetchedRef = useRef(false)

	useEffect(() => {
		// Only fetch if we have a token, no user data, and haven't fetched yet
		if (!token || user || hasFetchedRef.current) {
			return
		}

		// Mark as fetched immediately to prevent duplicate calls
		hasFetchedRef.current = true

		const fetchUser = async () => {
			try {
				const response = await userService.getCurrentUser({
					headers: {
						Authorization: `Bearer ${token}`,
					},
					params: {
						include:
							'account.royalty,account.plan,account.partnership,account.overdue,account.mrr,account.business_category.parent,account.billing_info,account.subscriptions.plan.prices,account.subscriptions.items,account.brand_identities,children_accounts',
					},
				})

				const fetchedUser = UserMapper.toEntity(response.data.data)
				dispatch(setUser(fetchedUser))

				// Restore previously selected account if one was persisted
				const storedAccountId = localStorage.getItem(SELECTED_ACCOUNT_ID_KEY)
				if (storedAccountId) {
					try {
						const accountResponse = await accountService.getAccount(
							Number(storedAccountId),
							{ params: { include: FULL_ACCOUNT_INCLUDES } }
						)
						const restoredAccount = AccountMapper.toEntity(
							accountResponse.data.data
						)
						dispatch(setSelectedAccount(restoredAccount))
					} catch {
						// Account is no longer accessible; clear the stale ID
						localStorage.removeItem(SELECTED_ACCOUNT_ID_KEY)
					}
				}
			} catch (error) {
				console.error('Failed to fetch user on initialization:', error)
				// Reset the flag so it can retry if needed
				hasFetchedRef.current = false
			}
		}

		fetchUser()
	}, [token, user, dispatch])
}
